import { useEffect, useRef, useState } from 'react';
import { PosProcessor } from '../engine/rppg/PosAlgorithm';
import { estimateBpm } from '../engine/rppg/FrequencyAnalyzer';
import { foreheadRoi, meanRgbFromImageData, type Landmark } from '../engine/rppg/SkinRoi';

export type RppgQuality = 'warming_up' | 'poor' | 'fair' | 'good';

export interface RppgState {
  /** Current heart-rate estimate in bpm, or `null` while warming up. */
  bpm: number | null;
  /** Smoothed bpm used for display (moving average over recent estimates). */
  displayBpm: number | null;
  /** Warm-up progress in [0, 1] — reaches 1 after ~10 seconds of valid frames. */
  warmupProgress: number;
  /** Qualitative signal quality. */
  quality: RppgQuality;
  /** Total frames that produced a valid ROI since the hook started. */
  framesProcessed: number;
}

const INITIAL_STATE: RppgState = {
  bpm: null,
  displayBpm: null,
  warmupProgress: 0,
  quality: 'warming_up',
  framesProcessed: 0,
};

// Tunables
const TARGET_FPS = 30;
const POS_WINDOW = 48;             // ~1.6s at 30fps
const PULSE_BUFFER_SIZE = 300;     // ~10s at 30fps
const MIN_WARMUP_FRAMES = 30 * 6;  // need ~6s of valid frames before first bpm
const BPM_UPDATE_MS = 1000;        // recompute bpm at 1 Hz
const DISPLAY_SMOOTH_N = 5;        // moving average over last 5 estimates

/**
 * rPPG heart-rate estimator.
 *
 * Pipeline:
 *   1. Every RAF tick, grab the forehead ROI from the current face
 *      landmarks and sample its mean RGB from a hidden offscreen canvas.
 *   2. Feed the RGB sample to a POS (Plane-Orthogonal-to-Skin) processor,
 *      which returns a pulse signal sample.
 *   3. Every ~1 second, run an FFT on the rolling pulse buffer and
 *      extract the peak frequency in the heart-rate band (42-180 bpm).
 *   4. Smooth the result over a small moving average for display.
 *
 * Needs ~6-10 seconds of stable face data before producing the first bpm
 * estimate. Best results when the subject is well-lit and mostly still.
 *
 * Consumers:
 *   - `videoElementId`: id of the <video> element streaming the webcam
 *   - `landmarksRef`: ref from useFaceLandmarker (.current is the latest
 *     NormalizedLandmark[] or null)
 *   - `enabled`: when false the RAF loop is not attached; switching to
 *     true resets the internal buffers so warm-up starts fresh.
 */
export function useRppg(
  videoElementId: string,
  landmarksRef: React.MutableRefObject<Landmark[] | null>,
  enabled: boolean,
): RppgState {
  const [state, setState] = useState<RppgState>(INITIAL_STATE);

  const posRef = useRef<PosProcessor>(new PosProcessor(POS_WINDOW, PULSE_BUFFER_SIZE));
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const lastBpmUpdateRef = useRef(0);
  const frameCountRef = useRef(0);
  const bpmHistoryRef = useRef<number[]>([]);

  useEffect(() => {
    if (!enabled) {
      // Reset on disable so warm-up starts fresh next time.
      posRef.current.reset();
      frameCountRef.current = 0;
      bpmHistoryRef.current = [];
      lastBpmUpdateRef.current = 0;
      setState(INITIAL_STATE);
      return;
    }

    // Lazy-create the offscreen canvas on first enable.
    if (!canvasRef.current) {
      const c = document.createElement('canvas');
      canvasRef.current = c;
      ctxRef.current = c.getContext('2d', { willReadFrequently: true });
    }

    let raf = 0;
    let lastFrameTime = 0;
    const minFrameIntervalMs = 1000 / TARGET_FPS;

    const tick = (now: number) => {
      raf = requestAnimationFrame(tick);

      // Throttle to TARGET_FPS (RAF can fire at 60+ Hz on some displays).
      if (now - lastFrameTime < minFrameIntervalMs) return;
      lastFrameTime = now;

      const video = document.getElementById(videoElementId) as HTMLVideoElement | null;
      const landmarks = landmarksRef.current;
      if (!video || video.readyState < 2 || !landmarks) return;

      const vw = video.videoWidth;
      const vh = video.videoHeight;
      if (!vw || !vh) return;

      const roi = foreheadRoi(landmarks, vw, vh);
      if (!roi) return;

      const canvas = canvasRef.current;
      const ctx = ctxRef.current;
      if (!canvas || !ctx) return;

      // Draw only the ROI region to a tiny offscreen canvas — much cheaper
      // than drawing the full frame and then cropping.
      canvas.width = roi.w;
      canvas.height = roi.h;
      try {
        ctx.drawImage(video, roi.x, roi.y, roi.w, roi.h, 0, 0, roi.w, roi.h);
      } catch {
        return; // video not ready / security error
      }

      let imageData: ImageData;
      try {
        imageData = ctx.getImageData(0, 0, roi.w, roi.h);
      } catch {
        return;
      }

      const [r, g, b] = meanRgbFromImageData(imageData);
      posRef.current.push(r, g, b);
      frameCountRef.current++;

      // Update bpm at 1 Hz
      if (now - lastBpmUpdateRef.current > BPM_UPDATE_MS) {
        lastBpmUpdateRef.current = now;

        const frames = frameCountRef.current;
        const warmupProgress = Math.min(1, frames / MIN_WARMUP_FRAMES);

        if (frames < MIN_WARMUP_FRAMES) {
          setState(prev => ({
            ...prev,
            warmupProgress,
            quality: 'warming_up',
            framesProcessed: frames,
          }));
          return;
        }

        const signal = posRef.current.getPulseSignal();
        const estimate = estimateBpm(signal, TARGET_FPS);

        if (!estimate) {
          setState(prev => ({
            ...prev,
            warmupProgress: 1,
            quality: 'poor',
            framesProcessed: frames,
          }));
          return;
        }

        // Push to smoothing history and compute moving average
        const history = bpmHistoryRef.current;
        history.push(estimate.bpm);
        if (history.length > DISPLAY_SMOOTH_N) history.shift();
        let avg = 0;
        for (const v of history) avg += v;
        avg /= history.length;

        const quality: RppgQuality =
          estimate.snr >= 0.2 ? 'good'
          : estimate.snr >= 0.12 ? 'fair'
          : 'poor';

        setState({
          bpm: Math.round(estimate.bpm),
          displayBpm: Math.round(avg),
          warmupProgress: 1,
          quality,
          framesProcessed: frames,
        });
      }
    };

    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
    };
  }, [enabled, videoElementId, landmarksRef]);

  return state;
}
